/*----------------------------------
- HOOK: USE COPY TO CLIPBOARD
----------------------------------*/

/**
 * Copies a string and says so for a moment: the `copied` flag a copy button turns into "Copied"
 * (the API page's endpoint button, the agenda's feed links). One timer per button, cleared on a
 * repeated click and on unmount, so a second click never cuts its own "Copied" short. A denied
 * clipboard goes to the app's error handler, which tells the visitor.
 */

import React from 'react';

import useContext from '@/client/context';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** How long "Copied" stays before the button reads its label again. */
const COPIED_FEEDBACK_MS = 1600;

/*----------------------------------
- HOOK
----------------------------------*/

export default function useCopyToClipboard(): { copied: boolean; copy: (value: string) => Promise<void> } {
    const { app } = useContext();
    const [copied, setCopied] = React.useState(false);
    const timer = React.useRef<number | undefined>(undefined);

    React.useEffect(() => () => window.clearTimeout(timer.current), []);

    const copy = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
        } catch (error) {
            app.handleError(error);
        }
    };

    return { copied, copy };
}
